import classNames from "classnames"

import { Color } from "../../../../constants/V2/color"
import { getImageDimensionsFromUrl } from "../../../../utils/image"
import DownloadAppButton from "../../../elements/V2/Buttons/DownloadAppButton"
import PillButton from "../../../elements/V2/Buttons/PillButton"
import TextButton from "../../../elements/V2/Buttons/TextButton"
import Callout, { CalloutProps } from "../../../elements/V2/Callout"
import GatsbyStoryblokImage from "../../../elements/V2/GatsbyStoryblokImage"
import Section from "../../../elements/V2/Section"
import Typography from "../../../elements/V2/Typography"
import Video from "../../../experiments/Video"
import HeroLayout from "../HeroLayout"

import PriceBadge, { PriceBadgeProps } from "@components/elements/V2/priceBadge"
import { useElementObserver } from "@hooks/V2/useElementObserver"
import { usePageLayoutState } from "@utils/layout"
import {
  getBackgroundColorClass,
  getAccentColor,
  getGradientFromColorClass,
  getGradientToColorClass,
} from "@utils/V2/color"
import React, { useRef, useEffect } from "react"

type Props = {
  headingText: string
  headingImage?: string
  headingSize?: "display1" | "h1" | "h2" | "h3" | "h4" | "h5"
  headingMobileAlignment?: "left" | "center"
  headingFontFamily?: "grotesk" | "grotesk-10" | "grotesk-25"
  bodyText: string
  superscriptText?: string
  primaryBackgroundColor: Color
  secondaryBackgroundColor?: Color
  priceBadge?: PriceBadgeProps
  primaryButtonText?: string
  primaryButtonLink?: string | YocoCom.YocoLink
  primaryButtonMobileText?: string
  primaryButtonMobileLink?: string | YocoCom.YocoLink
  primaryButtonBorderColor: Color
  secondaryButtonText: string
  secondaryCTATextColor: Color.White | Color.Charcoal
  secondaryButtonLink: string | YocoCom.YocoLink
  secondaryButtonMobileText?: string
  secondaryButtonMobileLink?: string | YocoCom.YocoLink
  videoUrl?: string
  imageUrl: string
  imageAlt: string
  mobileImage?: string
  callout?: CalloutProps
  showDownloadAppButton: boolean
  digitalPaymentsText?: string
  hasScrollArrow?: boolean
  hideMobileStickyButton?: boolean
  secondaryTrackingEvent: string
  secondaryTrackingEventKey?: string
  secondaryTrackingEventValue?: string
} & Partial<Storyblok.CTATrackingEvent>

const HalfHero = ({
  headingText,
  headingImage,
  mobileImage,
  headingSize = "display1",
  headingMobileAlignment = "left",
  headingFontFamily = "grotesk",
  bodyText,
  superscriptText,
  primaryBackgroundColor,
  secondaryBackgroundColor,
  priceBadge,
  primaryButtonText,
  primaryButtonLink,
  primaryButtonMobileLink,
  primaryButtonMobileText,
  primaryButtonBorderColor,
  secondaryButtonText,
  secondaryCTATextColor = Color.Charcoal,
  secondaryButtonLink,
  secondaryButtonMobileText,
  secondaryButtonMobileLink,
  videoUrl,
  imageUrl,
  imageAlt,
  callout,
  showDownloadAppButton = false,
  trackingEvent,
  trackingEventKey,
  trackingEventValue,
  digitalPaymentsText,
  hasScrollArrow = false,
  hideMobileStickyButton,
  secondaryTrackingEvent,
  secondaryTrackingEventKey,
  secondaryTrackingEventValue,
  ...props
}: Props) => {
  const textContainerRef = useRef(null)
  const { isVisible: isCtaVisible, startObserving } =
    useElementObserver(textContainerRef)
  const accentColor = getAccentColor(primaryBackgroundColor)
  const heroImageWidth =
    Number.parseInt(getImageDimensionsFromUrl(imageUrl).width) || undefined

  const { setHeroCTAVisible, hasAnchorNavigation } = usePageLayoutState()

  useEffect(() => {
    setHeroCTAVisible(isCtaVisible)

    return () => {
      setHeroCTAVisible(false)
    }
  }, [isCtaVisible, setHeroCTAVisible])

  startObserving()

  return (
    <HeroLayout
      primaryButtonText={primaryButtonMobileText ?? primaryButtonText}
      primaryButtonLink={primaryButtonLink}
      primaryButtonMobileLink={primaryButtonMobileLink}
      primaryButtonColor={primaryButtonBorderColor}
      hideMobileButton={hideMobileStickyButton}
      trackingEvent={trackingEvent}
      trackingEventKey={trackingEventKey}
      trackingEventValue={trackingEventValue}
    >
      <div
        className={classNames(
          "pb-lg -mb-lg grid items-center sm:grid-cols-2 sm:pb-104 sm:pt-80 md:pb-[128px] md:pt-104 lg:pb-104 lg:pt-72",
          hasAnchorNavigation && "sm:py-navHeight-xs md:py-0",
          !secondaryBackgroundColor
            ? getBackgroundColorClass(primaryBackgroundColor)
            : [
                "bg-gradient-to-br",
                getGradientFromColorClass(primaryBackgroundColor),
                getGradientToColorClass(secondaryBackgroundColor),
              ]
        )}
        {...props}
      >
        <Section className="flex flex-col pb-32 pt-72 sm:p-0">
          <div
            className={classNames(
              "flex flex-col sm:items-start sm:text-left",
              headingMobileAlignment === "left"
                ? "items-start"
                : "items-center text-center"
            )}
          >
            {superscriptText && (
              <Typography
                text={superscriptText}
                font="grotesk"
                weight="book"
                size="subscript-lg"
                color={accentColor}
                className="mb-8"
              />
            )}
            {callout && (
              <Callout
                color={getAccentColor(primaryBackgroundColor)}
                classname="mt-8 mb-16"
                text={callout.text}
                highlightedWord={callout.highlightedWord}
                highlightColor={callout.highlightColor}
                hasBorder={callout.hasBorder}
                hasIcon={callout.hasIcon}
                iconUrl={callout.iconUrl}
                iconAlt={callout.iconAlt}
                ctaText={callout.ctaText}
                ctaLink={callout.ctaLink}
                iconColor={callout.iconColor}
              />
            )}
            <div className="flex flex-col gap-24">
              <Typography
                text={headingText}
                font={headingFontFamily}
                weight="medium"
                size={headingSize}
                color={accentColor}
                element="h1"
                className={classNames("text-balance", { hidden: headingImage })}
              />
              {headingImage && (
                <GatsbyStoryblokImage
                  aspectRatio="1:1"
                  loadType="eager"
                  fetchPriority="high"
                  placeholderColor={primaryBackgroundColor}
                  image={headingImage}
                  alt={headingText}
                  quality={80}
                />
              )}
              <Typography
                text={bodyText}
                font="grotesk"
                weight="book"
                size="body-lg"
                color={accentColor}
              />
            </div>
          </div>

          {priceBadge?.text && (
            <PriceBadge
              text={priceBadge.text}
              highlight={priceBadge.highlight}
              price={priceBadge.price}
              backgroundColor={priceBadge.backgroundColor}
              classname="-mb-[18px] mt-24 pl-0"
            />
          )}

          <div
            ref={textContainerRef}
            className="mt-20 flex flex-col items-center gap-24 sm:mt-40 sm:flex-row sm:items-baseline"
          >
            {showDownloadAppButton ? (
              <DownloadAppButton
                text={primaryButtonText || "Download App"}
                color={primaryButtonBorderColor}
              />
            ) : primaryButtonText && primaryButtonLink ? (
              <PillButton
                size="large"
                style="solid"
                text={primaryButtonText}
                color={primaryButtonBorderColor}
                linkUrl={primaryButtonLink}
                mobileLinkUrl={primaryButtonMobileLink}
                mobileText={primaryButtonMobileText}
                trackingEvent={trackingEvent}
                trackingEventKey={trackingEventKey}
                trackingEventValue={trackingEventValue}
                hasScrollArrow={hasScrollArrow}
              />
            ) : null}
            <TextButton
              style="text"
              text={secondaryButtonText}
              linkUrl={secondaryButtonLink}
              mobileText={secondaryButtonMobileText}
              mobileLinkUrl={secondaryButtonMobileLink}
              color={secondaryCTATextColor}
              trackingEvent={secondaryTrackingEvent}
              trackingEventKey={secondaryTrackingEventKey}
              trackingEventValue={secondaryTrackingEventValue}
            />
          </div>
          {digitalPaymentsText && (
            <div className="mt-20 flex flex-col items-center gap-12 sm:mt-40 sm:items-start lg:flex-row">
              <div className="flex min-w-max gap-12">
                <img
                  src={"/icons/google-pay.svg"}
                  alt="Google Pay"
                  width={51}
                  height={20}
                  loading="lazy"
                  className="h-[68px] w-[73px] rounded-12 border border-charcoal/20 px-[11px] py-24"
                />
                <img
                  src={"/icons/apple-pay.svg"}
                  alt="Google Pay"
                  width={51}
                  height={20}
                  loading="lazy"
                  className="h-[68px] w-[73px] rounded-12 border border-charcoal/20 px-[11px] py-24"
                />
              </div>

              <Typography
                font="grotesk"
                weight="book"
                size="body-md"
                color={accentColor}
                className="max-w-[344px] opacity-80"
                text={digitalPaymentsText}
              />
            </div>
          )}
        </Section>

        <div
          className={classNames(
            "col-span-full sm:col-start-2 sm:col-end-3 sm:m-0",
            mobileImage ? "mb-40 mt-0" : "mt-40"
          )}
        >
          {videoUrl ? (
            <Video
              video={videoUrl}
              loadType="eager"
              aspectRatio="1:1"
              playOnMobile={true}
              coverImage={imageUrl}
              playbackMode="autoplay"
              className="flex items-center"
              ImageAlt={imageAlt || "Hero image"}
            />
          ) : (
            <>
              <GatsbyStoryblokImage
                loadType="eager"
                fetchPriority="high"
                aspectRatio="auto"
                width={heroImageWidth}
                placeholderColor={primaryBackgroundColor}
                image={imageUrl}
                alt={imageAlt || "Hero image"}
                quality={100}
                sizes="(min-width: 667px) 50vw, 100vw"
                srcSetWidths={[480, 640, 768, 1024, 1200, 1600, 2048]}
                className={classNames(
                  "sm:!block",
                  mobileImage ? "!hidden" : "!block"
                )}
              />
              {mobileImage && (
                <GatsbyStoryblokImage
                  loadType="eager"
                  fetchPriority="high"
                  aspectRatio="1:1"
                  width={heroImageWidth}
                  placeholderColor={primaryBackgroundColor}
                  image={mobileImage}
                  alt={imageAlt || "Hero image"}
                  quality={100}
                  sizes="(min-width: 667px) 50vw, 100vw"
                  srcSetWidths={[480, 640, 768]}
                  className="!block sm:!hidden"
                />
              )}
            </>
          )}
        </div>
      </div>
    </HeroLayout>
  )
}

export default HalfHero
